import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import "./App.css";
import "bootstrap/dist/css/bootstrap.css";
import App from "./App";
import AuthenticationButton from "./components/AuthenticationButton/AuthenticationButton";
import CompanyContext from "./auth/CompanyContext";
import CRMContext from "./auth/CRMContext";
import { useAuth0 } from "@auth0/auth0-react";
import { useHistory, useLocation } from "react-router-dom";
import { Container, Row, Col, Input } from "reactstrap";

export default function AppAuth(props) {
    const companyCtx = useContext(CompanyContext);
    const crmCtx = useContext(CRMContext);
    const setCRMChecking = crmCtx?.setCRMChecking;
    const setCRMAvailability = crmCtx?.setCRMAvailability;
    const clearCRMAvailability = crmCtx?.clearCRMAvailability;
    const crmChecked = crmCtx?.crmChecked;
    const { isAuthenticated, isLoading, user, getAccessTokenSilently, getIdTokenClaims } = useAuth0();

    const [initialAuth0, setInitialAuth0] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [clientToken, setClientToken] = useState(null);
    const [tokenLoaded, setTokenLoaded] = useState(false);
    const tokenRequestInFlightRef = useRef(false);
    const crmBlockedRef = useRef(false);
    const originalFetchRef = useRef(null);

    const { pathname } = useLocation();
    const history = useHistory();

    const parseJwt = useCallback((token) => {
        try {
            return JSON.parse(atob(token.split(".")[1]));
        } catch {
            return {};
        }
    }, []);

    const enrichCompaniesWithSystemSettings = useCallback(async (companyList, authToken) => {
        return Promise.all(companyList.map(async company => {
            try {
                const companyDatabase = company.company;
                const response = await fetch(`/api/systemCompanies/${companyDatabase}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + authToken,
                        'X-Database': companyDatabase
                    }
                });
                const results = await response.json();
                const settings = results?.data?.[0];
                return {
                    ...company,
                    companyName: settings?.CompanyName || company.label || company.company,
                    CompanyDisplayName: settings?.CompanyDisplayName || '',
                    companyDisplayName: settings?.CompanyDisplayName || ''
                };
            } catch {
                return {
                    ...company,
                    companyName: company.label || company.company,
                    CompanyDisplayName: company.CompanyDisplayName || '',
                    companyDisplayName: company.CompanyDisplayName || ''
                };
            }
        })).catch(() => companyList.map(company => ({
                ...company,
                companyName: company.label || company.company,
                CompanyDisplayName: company.CompanyDisplayName || '',
                companyDisplayName: company.CompanyDisplayName || ''
            })));
    }, []);

    const getCompanyOptionLabel = useCallback((company) => {
        const companyName = String(company?.companyName || company?.label || company?.company || '').trim();
        const displayName = String(company?.CompanyDisplayName || company?.companyDisplayName || '').trim();

        if (displayName && displayName !== companyName) {
            return `${companyName} (${displayName})`;
        }
        return companyName;
    }, []);

    const isPortalCRMRequest = useCallback((input) => {
        const crmBaseUrl = `${process.env.REACT_APP_PORTAL_API_URL}/api/crm`;
        const requestUrl = typeof input === 'string'
            ? input
            : input?.url || '';

        return String(requestUrl).startsWith(crmBaseUrl);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || originalFetchRef.current) return;

        const originalFetch = window.fetch.bind(window);
        originalFetchRef.current = originalFetch;

        window.fetch = (input, init) => {
            if (isPortalCRMRequest(input) && crmBlockedRef.current) {
                return Promise.resolve(new Response(JSON.stringify({
                    success: false,
                    code: 403,
                    message: 'CRM access is disabled for this session.'
                }), {
                    status: 403,
                    headers: { 'Content-Type': 'application/json' }
                }));
            }

            return originalFetch(input, init).then(response => {
                if (isPortalCRMRequest(input) && (response.status === 401 || response.status === 403)) {
                    crmBlockedRef.current = true;
                    setCRMAvailability?.(false);
                }

                return response;
            });
        };

        return () => {
            if (originalFetchRef.current) {
                window.fetch = originalFetchRef.current;
                originalFetchRef.current = null;
            }
        };
    }, [isPortalCRMRequest, setCRMAvailability]);

    useEffect(() => {
        crmBlockedRef.current = false;
    }, [selectedCompany, clientToken]);

    const getToken = useCallback(
        async (forceRefresh = false) => {
            if (tokenRequestInFlightRef.current) return;
            tokenRequestInFlightRef.current = true;

            try {
                const tokenOptions = forceRefresh ? { ignoreCache: true } : {};
                const nextClientToken = await getAccessTokenSilently(tokenOptions);

                const nextInitialAuth0 = user?.sub?.split("|")?.pop?.() ?? null;

                const claims = parseJwt(nextClientToken);
                const namespace = "https://sec-database-project.herokuapp.com/";
                const companiesFromClaims = claims[namespace + "companies"] || [];
                const activeCompaniesFromClaims = companiesFromClaims.filter(company => company.active !== false);

                const idTokenClaims = await getIdTokenClaims();
                const raw = idTokenClaims?.__raw || null;
                // eslint-disable-next-line no-console
                // console.log("Retrieved ID Token:", raw);

                if (props.idTokenCtx?.setIdToken) {
                    props.idTokenCtx.setIdToken(raw);
                }

                // Ensure at least one default company exists
                const baseCompanies =
                    companiesFromClaims.length === 0
                        ? [
                            {
                                company: "SecReporting",
                                role: "user",
                                active: true,
                                use_mfa: false,
                            },
                        ]
                        : activeCompaniesFromClaims;
                const nextCompanies = await enrichCompaniesWithSystemSettings(baseCompanies, nextClientToken);

                const storedCompany = companyCtx?.company ?? null;
                const validStoredCompany = storedCompany
                    ? nextCompanies.find((c) => c.company === storedCompany)
                    : null;

                if (validStoredCompany) {
                    setTokenLoaded(true);
                    setCompanies(nextCompanies);
                    setClientToken(nextClientToken);
                    setInitialAuth0(nextInitialAuth0);
                    setSelectedCompany(storedCompany);
                } else if (nextCompanies.length === 1) {
                    const onlyCompany = nextCompanies[0].company;
                    companyCtx?.setCompany?.(onlyCompany);

                    setTokenLoaded(true);
                    setCompanies(nextCompanies);
                    setClientToken(nextClientToken);
                    setInitialAuth0(nextInitialAuth0);
                    setSelectedCompany(onlyCompany);
                } else {
                    setTokenLoaded(true);
                    setCompanies(nextCompanies);
                    setClientToken(nextClientToken);
                    setInitialAuth0(nextInitialAuth0);
                    setSelectedCompany(null);
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Error retrieving token and company info:", err);
            } finally {
                tokenRequestInFlightRef.current = false;
            }
        },
        [
            companyCtx,
            enrichCompaniesWithSystemSettings,
            getAccessTokenSilently,
            getIdTokenClaims,
            parseJwt,
            props.idTokenCtx,
            user,
        ]
    );

    useEffect(() => {
        if (!isLoading && isAuthenticated && !tokenLoaded) {
            getToken(false);
        }
    }, [getToken, isAuthenticated, isLoading, tokenLoaded]);

    const resetCompany = useCallback(() => {
        companyCtx?.setCompany?.(null);
        clearCRMAvailability?.();

        sessionStorage.removeItem("advancedSearchResultsFiltered");
        sessionStorage.removeItem("advancedSearchResultsSorted");
        sessionStorage.removeItem("advancedSearchResultsItems");
        sessionStorage.removeItem("advancedSearchShowResults");
        sessionStorage.removeItem("advancedSearchGroupSelections");

        localStorage.removeItem("demographicsActiveTab");
        localStorage.removeItem("searchNotesIsOpen");
        localStorage.removeItem("searchStatisticsIsOpen");
        localStorage.removeItem("selectedCompany");

        setSelectedCompany(null);
        history.push("/");
    }, [companyCtx, clearCRMAvailability, history]);

    const handleCompanySelect = useCallback(
        (e) => {
            const next = e.target.value;
            clearCRMAvailability?.();
            companyCtx?.setCompany?.(next);
            setSelectedCompany(next);
            history.push("/");
        },
        [clearCRMAvailability, companyCtx, history]
    );

    const hasCompanyPortalDatabase = useCallback((companySettings) => {
        return String(companySettings?.CompanyPortalDatabase || "").trim() !== "";
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            clearCRMAvailability?.();
            return;
        }

        const authToken = props.idTokenCtx?.idToken || clientToken;
        const crmCompany = companyCtx?.company || selectedCompany;

        if (!tokenLoaded || !selectedCompany || !authToken || !crmCompany) {
            return;
        }

        let cancelled = false;
        setCRMChecking?.();

        fetch(`/api/systemCompanies/${crmCompany}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + authToken,
                'X-Database': crmCompany
            }
        })
            .then((response) => response.json())
            .then((results) => {
                if (cancelled) return null;

                const companySettings = results?.data?.[0];
                if (!hasCompanyPortalDatabase(companySettings)) {
                    setCRMAvailability?.(false);
                    return null;
                }

                return fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + authToken,
                        'X-Database': crmCompany
                    }
                });
            })
            .then((response) => {
                if (cancelled || !response) return;
                setCRMAvailability?.(response.status === 200);
            })
            .catch(() => {
                if (cancelled) return;
                setCRMAvailability?.(false);
            });

        return () => {
            cancelled = true;
        };
    }, [
        isAuthenticated,
        tokenLoaded,
        selectedCompany,
        clientToken,
        props.idTokenCtx?.idToken,
        companyCtx?.company,
        clearCRMAvailability,
        hasCompanyPortalDatabase,
        setCRMChecking,
        setCRMAvailability
    ]);


    if (pathname === "/callback") {
        return null;
    }

    if (!isAuthenticated) {
        return (
            <nav className="Nav">
                <Container fluid>
                    <Row>
                        <Col className="text-right">
                            <AuthenticationButton />
                        </Col>
                    </Row>
                </Container>
            </nav>
        );
    }

    if (!tokenLoaded) {
        return <div className="App">Loading...</div>;
    }

    if (!selectedCompany) {
        return (
            <>
                <nav className="Nav">
                    <Container fluid>
                        <Row>
                            <Col className="text-right">
                                <AuthenticationButton />
                            </Col>
                        </Row>
                    </Container>
                </nav>
                <Container>
                    <Row>
                        <Col className="text-center">
                            {companies.length > 0 ?
                                <>
                                    <h4>Select Company:</h4>
                                    <Input type="select" onChange={handleCompanySelect} defaultValue="">
                                        <option value="" disabled>
                                            Select one
                                        </option>
                                        {companies.map((c) => (
                                            <option key={c.company} value={c.company}>
                                                {getCompanyOptionLabel(c)}
                                            </option>
                                        ))}
                                    </Input>
                                </>
                                :
                                <>
                                    <h4>No active companies available</h4>
                                    <p>Your account is not currently assigned to an active company.</p>
                                </>
                            }
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    if (!crmChecked) {
        return <div className="App">Loading...</div>;
    }

    return (
        <App
            auth0={initialAuth0}
            clientToken={clientToken}
            company={selectedCompany}
            idToken={props.idTokenCtx?.idToken}
            resetCompany={resetCompany}
            getToken={getToken}
            companies={companies}
        />
    );
}
